/**
 * usePopupPerformanceTracking - Sentry performance tracking for popup windows
 *
 * Tracks the full popup lifecycle:
 * - Popup initialization (mount to data request)
 * - Data fetch (request sent to response received)
 * - Time to interactive (mount to data rendered)
 *
 * All spans are tagged with popup metadata for easy filtering in Sentry.
 */
import { useCallback, useRef, useEffect } from 'react';
import * as Sentry from '@sentry/react';

interface PopupTrackingOptions {
  /** Popup ID from URL */
  popupId: string | null;
  /** Type of popup (report, targets, events, etc.) */
  popupType: string;
  /** Additional metadata to attach to all spans */
  metadata?: Record<string, string | number | boolean>;
}

interface PopupTrackingResult {
  /** Call when data request is sent to main window */
  trackDataRequest: () => void;
  /** Call when data response is received from main window */
  trackDataReceived: (recordCount?: number) => void;
  /** Call when popup encounters an error */
  trackError: (error: Error | string) => void;
  /** Call when popup times out waiting for data */
  trackTimeout: () => void;
}

// Standard tags applied to all popup spans
const getPopupTags = (popupType: string, popupId: string | null) => ({
  'window.type': 'popup',
  'popup.type': popupType,
  'popup.id': popupId || 'unknown',
});

export function usePopupPerformanceTracking(
  options: PopupTrackingOptions,
): PopupTrackingResult {
  const { popupId, popupType, metadata } = options;

  // Track timestamps for duration calculations
  const mountTimeRef = useRef<number>(Date.now());
  const requestTimeRef = useRef<number | null>(null);

  // Track the lifecycle span
  const lifecycleSpanRef = useRef<Sentry.Span | null>(null);

  // Start lifecycle span on mount
  useEffect(() => {
    mountTimeRef.current = Date.now();

    // Start a manual span for the entire popup lifecycle
    Sentry.startSpanManual(
      {
        name: `popup.lifecycle:${popupType}`,
        op: 'popup.lifecycle',
        attributes: {
          ...getPopupTags(popupType, popupId),
          ...metadata,
        },
      },
      (span) => {
        lifecycleSpanRef.current = span;
      },
    );

    // Add breadcrumb for popup open
    Sentry.addBreadcrumb({
      category: 'popup',
      message: `Popup opened: ${popupType}`,
      level: 'info',
      data: {
        popupId,
        popupType,
        ...metadata,
      },
    });

    // Cleanup on unmount
    return () => {
      if (lifecycleSpanRef.current) {
        lifecycleSpanRef.current.end();
        lifecycleSpanRef.current = null;
      }
    };
  }, [popupId, popupType, metadata]);

  // Track when data request is sent
  const trackDataRequest = useCallback(() => {
    requestTimeRef.current = Date.now();
    const timeSinceMount = requestTimeRef.current - mountTimeRef.current;

    Sentry.startSpan(
      {
        name: `popup.init:${popupType}`,
        op: 'popup.init',
        attributes: {
          ...getPopupTags(popupType, popupId),
          'popup.time_to_request_ms': timeSinceMount,
          ...metadata,
        },
      },
      () => {
        // Span auto-closes, we just want to record the event
      },
    );

    Sentry.addBreadcrumb({
      category: 'popup',
      message: `Data requested: ${popupType}`,
      level: 'info',
      data: {
        popupId,
        timeSinceMount,
      },
    });
  }, [popupId, popupType, metadata]);

  // Track when data is received
  const trackDataReceived = useCallback(
    (recordCount?: number) => {
      const now = Date.now();
      const timeSinceMount = now - mountTimeRef.current;
      const timeSinceRequest = requestTimeRef.current
        ? now - requestTimeRef.current
        : null;

      // Record the data fetch span
      Sentry.startSpan(
        {
          name: `popup.data_received:${popupType}`,
          op: 'popup.data',
          attributes: {
            ...getPopupTags(popupType, popupId),
            'popup.time_to_data_ms': timeSinceMount,
            'popup.data_fetch_duration_ms': timeSinceRequest ?? 0,
            'popup.record_count': recordCount ?? 0,
            ...metadata,
          },
        },
        () => {
          // Span auto-closes
        },
      );

      // End the lifecycle span successfully
      if (lifecycleSpanRef.current) {
        lifecycleSpanRef.current.setStatus({ code: 0, message: 'Success' });
        lifecycleSpanRef.current.setAttribute(
          'popup.time_to_interactive_ms',
          timeSinceMount,
        );
        lifecycleSpanRef.current.setAttribute(
          'popup.record_count',
          recordCount ?? 0,
        );
        lifecycleSpanRef.current.end();
        lifecycleSpanRef.current = null;
      }

      Sentry.addBreadcrumb({
        category: 'popup',
        message: `Data received: ${popupType}`,
        level: 'info',
        data: {
          popupId,
          timeSinceMount,
          timeSinceRequest,
          recordCount,
        },
      });
    },
    [popupId, popupType, metadata],
  );

  // Track errors
  const trackError = useCallback(
    (error: Error | string) => {
      const now = Date.now();
      const timeSinceMount = now - mountTimeRef.current;
      const errorMessage = error instanceof Error ? error.message : error;

      // End lifecycle span with error
      if (lifecycleSpanRef.current) {
        lifecycleSpanRef.current.setStatus({ code: 2, message: errorMessage });
        lifecycleSpanRef.current.setAttribute(
          'popup.time_to_error_ms',
          timeSinceMount,
        );
        lifecycleSpanRef.current.end();
        lifecycleSpanRef.current = null;
      }

      // Capture as exception with popup context
      Sentry.withScope((scope) => {
        scope.setTags(getPopupTags(popupType, popupId));
        scope.setExtra('timeSinceMount', timeSinceMount);
        scope.setExtra('metadata', metadata);

        if (error instanceof Error) {
          Sentry.captureException(error);
        } else {
          Sentry.captureMessage(errorMessage, 'error');
        }
      });

      Sentry.addBreadcrumb({
        category: 'popup',
        message: `Error in popup: ${popupType}`,
        level: 'error',
        data: {
          popupId,
          error: errorMessage,
          timeSinceMount,
        },
      });
    },
    [popupId, popupType, metadata],
  );

  // Track timeout
  const trackTimeout = useCallback(() => {
    const now = Date.now();
    const timeSinceMount = now - mountTimeRef.current;

    // End lifecycle span with timeout
    if (lifecycleSpanRef.current) {
      lifecycleSpanRef.current.setStatus({
        code: 2,
        message: 'Timeout waiting for data',
      });
      lifecycleSpanRef.current.setAttribute('popup.timed_out', true);
      lifecycleSpanRef.current.setAttribute(
        'popup.time_to_timeout_ms',
        timeSinceMount,
      );
      lifecycleSpanRef.current.end();
      lifecycleSpanRef.current = null;
    }

    Sentry.addBreadcrumb({
      category: 'popup',
      message: `Timeout in popup: ${popupType}`,
      level: 'warning',
      data: {
        popupId,
        timeSinceMount,
      },
    });
  }, [popupId, popupType]);

  return {
    trackDataRequest,
    trackDataReceived,
    trackError,
    trackTimeout,
  };
}
